import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, type Category } from '@/mocks/categories';

export default function CategoriesPage() {
  const location = useLocation();
  const [activeSlug, setActiveSlug] = useState<string>(categories[0].slug);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && categories.some((c) => c.slug === hash)) {
      setActiveSlug(hash);
    }
  }, [location.hash]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const handleTabClick = (slug: string) => {
    setActiveSlug(slug);
    const el = document.getElementById(slug);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '久久插产品分类 —— 六大专业插接系列',
    description: '久久插六大专业插接系列：工业级重型、防振自锁、高速数据、防水户外、微型精密、模块化定制，全方位插接解决方案。',
    url: 'https://www.jiujiucha.com/categories',
  };

  return (
    <>
      <SEOHead
        title="久久插产品分类 —— 六大专业插接系列 | 工业级防松脱插接配件"
        description="久久插六大专业插接系列产品分类页面：工业级重型插接件、防振自锁插接件、高速数据传输插接件、防水户外插接件、微型精密插接件、模块化定制插接方案。全方位持久稳固连接解决方案。"
        keywords="久久插产品,工业级插接件,防振自锁插接件,防水插接件,高速数据插接件,微型精密插接件,模块化插接方案"
        canonicalUrl="https://www.jiujiucha.com/categories"
        schema={schema}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '产品分类' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-4 mb-3">
              产品分类
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
              久久插六大专业插接系列，从工业重载到微型精密，为您提供全方位的持久稳固连接解决方案。选择久久插，品质保障有口皆碑。
            </p>
          </div>
        </div>

        <div className="sticky top-16 md:top-18 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabClick(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categories.map((cat) => (
              <article
                key={cat.slug}
                id={cat.slug}
                className={`mb-12 md:mb-16 last:mb-0 scroll-mt-40 ${
                  activeSlug !== cat.slug ? 'hidden' : ''
                }`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
                  <div className="relative rounded-xl overflow-hidden h-64 md:h-80 lg:h-96">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                        <i className={`${cat.icon} text-xl text-accent-600`}></i>
                      </div>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                        {cat.name}
                      </h2>
                    </div>
                    <p className="text-foreground-600 leading-relaxed mb-6 text-sm md:text-base">
                      {cat.description}
                    </p>

                    <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-5">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                        <i className="ri-check-double-line text-accent-600"></i>
                        核心特性
                      </h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {cat.features.map((feature) => (
                          <li
                            key={feature}
                            className="flex items-start gap-2 text-sm text-foreground-700"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-1.5 flex-shrink-0"></span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                        <i className="ri-building-line text-accent-600"></i>
                        典型应用场景
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {cat.applications.map((app) => (
                          <span
                            key={app}
                            className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700 font-medium"
                          >
                            {app}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-6">
                      <Link
                        to="/about"
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                      >
                        <i className="ri-mail-send-line"></i>
                        咨询该系列产品
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              没有找到合适的产品？
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mb-6 max-w-lg mx-auto">
              久久插模块化定制系列支持灵活配置，电源、信号、气动、光纤混合定制，满足您的特殊需求。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg border-2 border-foreground-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-customer-service-2-line"></i>
              联系我们定制方案
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}